import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import "./AddIndustry.css";

const Industry = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`${API_BASE}/reference-values/`);
      const allValues = res.data?.data || [];

      const industryOnly = allValues.filter(
        (item) => item.option_set_value?.option_type === "INDUSTRY"
      );

      industryOnly.sort(
        (a, b) =>
          (a.sort_order || 0) - (b.sort_order || 0) ||
          a.label.localeCompare(b.label)
      );

      setIndustries(industryOnly);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching industries:", error);
      Swal.fire("Error", "Failed to load industries", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This industry will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/reference-values/${id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Industry has been removed.",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchIndustries();
    } catch (error) {
      console.error("Error deleting industry:", error);

      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to delete industry",
        "error"
      );
    }
  };

  const handleEdit = (item) => {
    navigate(`/catalog/edit-industry/${item.id}`);
  };

  const filteredIndustries = industries.filter(
    (item) =>
      item.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* Pagination */
  const totalEntries = filteredIndustries.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(startIndex + entriesPerPage, totalEntries);

  const currentIndustries = filteredIndustries.slice(
    startIndex,
    startIndex + entriesPerPage
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="industry-page-content">
          <div className="industry-table-header">
            <div className="industry-header-left">
              <h2>Industry Management</h2>

              <span className="industry-count">
                {industries.length} industries
              </span>
            </div>

            <div className="industry-header-right">
              <div className="industry-search-wrapper">
                <FiSearch className="industry-search-icon" />

                <input
                  type="text"
                  placeholder="Search industries..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="industry-search-input"
                />
              </div>

              <button
                className="industry-refresh-btn"
                onClick={fetchIndustries}
                title="Refresh"
              >
                <FiRefreshCw />
              </button>

              <button
                className="industry-add-btn"
                onClick={() => navigate("/catalog/add-industry")}
              >
                <FiPlus />
                Add Industry
              </button>
            </div>
          </div>

          {loading ? (
            <div className="industry-loader-container">
              <div className="industry-loader">
                <div className="industry-spinner"></div>
                <p>Loading industries...</p>
              </div>
            </div>
          ) : (
            <div className="industry-table-container">
              <table className="industry-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Code</th>
                    <th>Sort Order</th>
                    <th>Status</th>
                    <th className="industry-actions-header">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {currentIndustries.length > 0 ? (
                    currentIndustries.map((item) => (
                      <tr key={item.id}>
                        <td className="industry-name-cell">
                          <div className="industry-name-wrapper">
                            <div className="industry-avatar">
                              {item.label?.charAt(0).toUpperCase()}
                            </div>

                            <span>{item.label}</span>
                          </div>
                        </td>

                        <td>
                          <span className="industry-code-badge">
                            {item.code}
                          </span>
                        </td>

                        <td>
                          <span className="industry-sort-order">
                            {item.sort_order ?? 0}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`industry-status ${
                              item.is_active ? "active" : "inactive"
                            }`}
                          >
                            <span className="industry-status-dot"></span>

                            {item.is_active ? "ACTIVE" : "INACTIVE"}
                          </span>
                        </td>

                        <td className="industry-actions-cell">
                          <button
                            className="industry-action-btn industry-edit-btn"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            className="industry-action-btn industry-delete-btn"
                            onClick={() => handleDelete(item.id)}
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="industry-no-data">
                        <div className="industry-empty-state">
                          <div className="industry-empty-icon">
                            <FiSearch />
                          </div>

                          <p>No industries found</p>

                          <span className="industry-empty-sub">
                            {searchTerm
                              ? "Try adjusting your search"
                              : "No industries are available"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalEntries > 0 && (
                <div className="industry-pagination">
                  <div className="industry-show-entries">
                    <span>Show</span>

                    <select
                      value={entriesPerPage}
                      onChange={handleEntriesChange}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>

                    <span>entries</span>
                  </div>

                  <div className="industry-pagination-info">
                    Showing <strong>{startIndex + 1}</strong> to{" "}
                    <strong>{endIndex}</strong> of{" "}
                    <strong>{totalEntries}</strong> entries
                  </div>

                  {totalPages > 1 && (
                    <div className="industry-pagination-controls">
                      <button
                        className="industry-page-btn industry-prev-btn"
                        onClick={handlePrevious}
                        disabled={currentPage === 1}
                        title="Previous"
                      >
                        <FiChevronLeft />
                      </button>

                      {getPageNumbers().map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="industry-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            className={`industry-page-btn ${
                              currentPage === page ? "active" : ""
                            }`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        )
                      )}

                      <button
                        className="industry-page-btn industry-next-btn"
                        onClick={handleNext}
                        disabled={currentPage === totalPages}
                        title="Next"
                      >
                        <FiChevronRight />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Industry;